import { useEffect } from "react";
import { ArrowUpRight, BookOpen } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import { BUYERS_GUIDE, SELLERS_GUIDE, trackGuideDownload } from "@/lib/guides";

const TITLE = "Home Buyer & Seller Guides | Hadassah Homes";
const DESCRIPTION =
  "Download Hadassah Homes’ home buyer and seller guides for practical guidance on navigating the Massachusetts real estate process.";

const guides = [BUYERS_GUIDE, SELLERS_GUIDE];

const Guides = () => {
  useEffect(() => {
    document.title = TITLE;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", DESCRIPTION);
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = DESCRIPTION;
      document.head.appendChild(meta);
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/resources/guides";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        title="Guides"
        subtitle="Practical, no-pressure guides to help you understand the buying and selling process before you begin."
      />

      <ContentSection>
        <div className="mx-auto max-w-wide">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {guides.map((guide) => (
              <article
                key={guide.key}
                className="flex flex-col border border-border bg-card p-7 md:p-9"
              >
                <BookOpen className="mb-5 h-6 w-6 text-accent" strokeWidth={1.5} aria-hidden="true" />
                <h2 className="font-headline text-2xl text-foreground md:text-[1.75rem]">
                  {guide.title}
                </h2>
                <p className="mt-4 text-base leading-[1.8] text-muted-foreground">
                  {guide.description}
                </p>
                <div className="mt-auto pt-8">
                  <a
                    href={guide.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={guide.ariaLabel}
                    onClick={() => trackGuideDownload(guide, "resources-guides")}
                    className="inline-flex items-center gap-2 bg-accent px-6 py-3.5 font-body text-xs uppercase tracking-[0.16em] text-accent-foreground transition-colors duration-300 hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {guide.cta}
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
                  </a>
                  <p className="mt-3 text-xs text-muted-foreground">PDF · Opens in a new tab</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </ContentSection>
    </SiteLayout>
  );
};

export default Guides;
